import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import moment from 'moment-timezone';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';

import { PaginationMetadataViewModel } from '@models/pagination';
import { TransactionDetailViewModel } from '@models/transaction.class';
import { TradeService } from 'src/core';

interface PaginatedSearchQuery {
  startDate: Date;
  endDate: Date;
  page: number;
  isPageChange?: boolean;
}

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'movements-usa',
  templateUrl: 'movements-usa.component.html',
  styleUrls: ['./movements-usa.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class MovementsUsaComponent implements OnInit, OnDestroy {
  public readonly defaultStartDate = moment()
    .tz('America/Mexico_City')
    .subtract(30, 'd')
    .startOf('day')
    .toDate();

  public readonly defaultEndDate = moment()
    .tz('America/Mexico_City')
    .endOf('day')
    .toDate();

  public readonly showSkeleton$: Observable<boolean>;
  public readonly emptyState = [1, 2, 3];
  public transactions: TransactionDetailViewModel[] = [];
  public pagination: PaginationMetadataViewModel;

  public requestFailed = false;

  private isMovementsLoading$ = new BehaviorSubject<boolean>(false);
  private destroyed$ = new Subject();

  private newSearch$: BehaviorSubject<Partial<PaginatedSearchQuery>> =
    new BehaviorSubject({
      startDate: this.defaultStartDate,
      endDate: this.defaultEndDate,
      page: 1,
    });

  constructor(private tradeService: TradeService) {
    this.showSkeleton$ = this.isMovementsLoading$
      .asObservable()
      .pipe(shareReplay(1));
  }

  ngOnInit() {
    this.newSearch$
      .pipe(
        tap(() => this.isMovementsLoading$.next(true)),
        switchMap((queryFilters: PaginatedSearchQuery) =>
          this.tradeService.getMovementsUsa(queryFilters).pipe(
            takeUntil(this.destroyed$),
            map((response) => ({
              ...response,
              isPageChange: queryFilters.isPageChange,
            })),
          ),
        ),
        tap(() => {
          this.isMovementsLoading$.next(false);
          this.requestFailed = false;
        }),
      )
      .subscribe(
        ({
          transactions,
          pagination,
          isPageChange,
        }: {
          transactions: TransactionDetailViewModel[];
          pagination: PaginationMetadataViewModel;
          isPageChange: boolean;
        }) => {
          this.updateTransactions(transactions, isPageChange);
          this.pagination = pagination;
        },
        () => {
          this.isMovementsLoading$.next(false);
          this.requestFailed = true;
        },
      );
  }

  ngOnDestroy() {
    this.destroyed$.next();
  }

  public handleDateRangeChange(dateRange: { startDate: Date; endDate: Date }) {
    this.newSearch$.next({
      startDate: moment(dateRange.startDate).startOf('day').toDate(),
      endDate: moment(dateRange.endDate).endOf('day').toDate(),
      page: 1,
    });
  }

  public getNextPage() {
    if (this.pagination.next) {
      this.newSearch$.next({
        // Use the existing newSearch to prevent us from using undefined dates
        ...this.newSearch$.value,
        page: this.pagination.next,
        isPageChange: true,
      });
    }
  }

  private updateTransactions(
    transactions: TransactionDetailViewModel[],
    isPageChange: boolean,
  ) {
    const requestedTransactions = transactions;

    if (isPageChange) {
      this.transactions = [...this.transactions, ...requestedTransactions];
    } else {
      this.transactions = requestedTransactions;
    }

    this.transactions = this.transactions.sort(
      (a, b) => a.date.localeCompare(b.date) || a.hour.localeCompare(b.hour),
    );
  }
}
